// 로그인 세션 흔적(쿠키 + localStorage) 제거 단일 진입점.
// 명시적 로그아웃(headerView.logout)과 토큰 만료 강제 재로그인(router redirectToLogin)의 삭제 범위를 한 곳에 모은다.
// 각자 지우게 두면 한쪽만 갱신돼 인증 토큰이 남는다(= 회사 판별이 2계통으로 갈라져 있던 버그와 같은 종류의 실수).
// 자격증명은 두 경로가 항상 같은 범위를 지우고, 결제 핸드오프 값만 includePaymentHandoff 로 갈라진다(아래 참고).
// store 를 import 하지 않는다(순환 참조 방지: store → api/loginAPI → utils/demoCompany → utils/clientToken).
// 그래서 Pinia 초기화는 여기서 하지 않는다 — 필요하면 호출부에서 수행한다.
import VueCookies from 'vue-cookies'
import Cookies from 'js-cookie'

// 상위 도메인(.infocarbiz.com) 공유 쿠키.
// 심을 때와 동일한 domain/path 를 지정해야 실제로 삭제된다(불일치하면 브라우저가 다른 쿠키로 보고 지우지 않는다).
// SignUpView.clearAuthCookies 와 같은 패턴.
const SHARED_COOKIE_OPTIONS = { path: '/', domain: '.infocarbiz.com' }

// 결제 핸드오프 쿠키: 상위 도메인(.infocarbiz.com)을 공유하는 결제 배너(별도 repo `banner/`, PHP)와 주고받는 값들.
// 명시적 로그아웃일 때만 지운다. 만료/미인증 리다이렉트에서 지우면 안 되는 이유는 clearSession 주석 참고.
//
// auth_token 이 자격증명이 아니라 여기 묶여 있는 이유: SPA 가 심은 사본이 아니라
// 배너가 자체 발급·사용·삭제하는 "배너 소유" 쿠키라 수명을 SPA 세션에 묶으면 안 된다.
// - 배너 login_processor.php 가 SPA 와 무관하게 setcookie("auth_token", ...) 로 .infocarbiz.com 에 직접 심는다.
// - 배너 tail.php / head.php 의 로그아웃 핸들러와 orderview.php 진입부가 자기 시점에 직접 지운다.
// - order.php / new_order.php 는 이 쿠키가 없으면 결제 페이지 진입을 막고 배너 로그인으로 되돌린다(진입 게이트).
// - 결정적으로 nicepay2/euc-kr/billApproveResult.php 는 NicePay 승인 성공(ResultCode == "3001") "이후"
//   progress_id 와 auth_token 을 같은 블록에서 함께 읽어, 요금제 반영 PUT /web/plan/v1/progress/{progress_id} 를
//   auth_token 헤더로 호출한다. 두 값은 한 쌍으로만 의미가 있어서, progress_id 만 남기고 auth_token 을 지우면
//   핸드오프 보호가 통째로 무효가 된다 — 결제는 청구됐는데 요금제만 미반영되고 재로그인으로 복구되지 않는다.
// SPA(LoginView 결제정보 무효 분기 / BizPayView)도 같은 이름으로 심지만 그건 배너에 넘기기 위한 것이다.
// 보안 목적은 명시적 로그아웃에서 그대로 유지된다(includePaymentHandoff: true 로 이 배열 전체를 지운다).
const PAYMENT_HANDOFF_COOKIE_KEYS = ['auth_token', 'biz_car', 'progress_id', 'plan_type']

// 세션 한정 localStorage 키.
// lastCompanyCode 는 로그인 화면 회사코드 프리필 "전용" 키라 의도적으로 제외한다(로그아웃 후에도 유지가 의도된 동작).
const SESSION_STORAGE_KEYS = [
  'admin_name',
  'companyName',
  'companyCode',
  'loginAccount',
  'loglevel',
  'planType'
]

// 라우터 가드 안에서도 호출되므로 동기 함수로 유지한다(네비게이션 흐름을 붙잡지 않는다).
// 여기서 지우는 값 중 requireAuth 가 읽는 것은 client_token / refresh_token 뿐이고,
// 둘 다 제거 후 /login 은 publicPages 라 재진입 시 다시 redirectToLogin 으로 들어가지 않는다(무한 리다이렉트 없음).
//
// includePaymentHandoff 로 범위를 나누는 이유(호출 경로가 두 종류라서다):
// - headerView.logout() = 사용자가 명시적으로 끝낸 세션 → true. 결제 진행 상태까지 전부 정리한다.
// - router redirectToLogin() = 토큰 만료 + "미로그인 상태로 보호 라우트 접근"까지 포함 → 기본값 false.
//   후자가 중요하다. 결제가 필요한 계정은 로그인 시 LoginView 가 auth_token 을 상위 도메인에 심고
//   /gotopayment 로 보내며, 사용자는 거기서 결제 배너를 target="_blank" 새 탭으로 연다.
//   이때 원래 SPA 탭에서 아무 보호 라우트로 이동하기만 해도 이 가드가 돌기 때문에,
//   결제 핸드오프 값까지 지우면 아직 살아 있는 배너 탭의 결제 플로우가 끊긴다.
//   auth_token 도 배너가 소유·소비하는 값이라 이 경로에서는 남긴다(PAYMENT_HANDOFF_COOKIE_KEYS 주석 참고).
//   SPA 세션 자격증명(client_token/refresh_token/loginData)은 두 경로 모두에서 항상 지우므로,
//   "만료된 SPA 세션이 그대로 살아 있는" 상태는 어느 경우에도 생기지 않는다.
export const clearSession = ({ includePaymentHandoff = false } = {}) => {
  VueCookies.remove('client_token')
  VueCookies.remove('refresh_token')
  VueCookies.remove('loginData')

  if (includePaymentHandoff) {
    PAYMENT_HANDOFF_COOKIE_KEYS.forEach((cookieKey) =>
      Cookies.remove(cookieKey, SHARED_COOKIE_OPTIONS)
    )
  }

  SESSION_STORAGE_KEYS.forEach((storageKey) => localStorage.removeItem(storageKey))
}
